/**
 * Shared locale / timezone / currency defaults and pure mapping helpers.
 *
 * Single source of truth for the fallback values used when a user has not
 * stored an explicit preference. Both the web client and the server (which
 * feeds the AI concierge prompt) import these, so the UI and the concierge
 * never disagree about which timezone/currency/locale to assume. A future
 * React Native client imports the same module for free.
 *
 * No React or Firebase deps — safe to import from client, server, and tests.
 */

import countryToCurrency from 'country-to-currency';

/**
 * Canonical fallback timezone (IANA name) when a user has no stored
 * `attributes.timezone`. Consumed by the client UI getters and by the server
 * when building the concierge prompt + `current_time`, so both reason about
 * time in the same zone instead of drifting (client UTC vs server NY).
 */
export const DEFAULT_TIMEZONE = 'America/New_York';

/** Canonical fallback currency (ISO 4217) when a locale yields no known currency. */
export const DEFAULT_CURRENCY = 'USD';

/** Canonical fallback locale (BCP 47) when browser detection is unavailable. */
export const DEFAULT_LOCALE = 'en-US';

/** Canonical fallback country code (ISO 3166-1 alpha-2) when a user has no address. */
export const DEFAULT_COUNTRY_CODE = 'US';

/**
 * Maps a BCP 47 locale (e.g. `'en-US'`) to an ISO 4217 currency code
 * (e.g. `'USD'`) by extracting the region subtag and looking it up in the
 * `country-to-currency` table. Falls back to {@link DEFAULT_CURRENCY} when the
 * locale carries no region subtag or the region has no known currency.
 *
 * @example
 * currencyForLocale('en-GB'); // 'GBP'
 * currencyForLocale('fr');    // 'USD' (no region subtag)
 */
export function currencyForLocale(locale: string | undefined): string {
  // The package ships a `const` map with literal value types and no index
  // signature, so cast to a plain record before indexing by arbitrary region.
  const parts = (locale || DEFAULT_LOCALE).split('-');
  if (parts.length < 2) return DEFAULT_CURRENCY;
  const region = parts[1]!.toUpperCase();
  return (countryToCurrency as Record<string, string>)[region] || DEFAULT_CURRENCY;
}
